import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import HotelIcon from '@mui/icons-material/Hotel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VillaIcon from '@mui/icons-material/Villa';
import SectionHeading from '../SectionHeading';
import type { ArchiveLocationGroup, ArchiveTypeGroup, ProjectRecord } from '../../data/projects';
import { portfolioImgSrc, portfolioSrcSet, stemFromPublicImagePath } from '../../lib/responsiveWebp';

const numberFormatter = new Intl.NumberFormat('en-US');
const FEATURED_MEDIA_SIZES = '(max-width: 900px) 100vw, (max-width: 1200px) 50vw, 520px';
const SFR_GROUP_KEY = 'incline-sfr-custom';
const categoryIcons = {
  commercial: StorefrontIcon,
  hotel: HotelIcon,
  mixedUse: CorporateFareIcon,
  residential: ApartmentIcon,
} satisfies Record<ProjectRecord['typeCategory'], typeof ApartmentIcon>;

function formatSquareFeet(value: number | null): string | null {
  return value === null ? null : `${numberFormatter.format(value)} sq ft`;
}

function formatUnits(project: ProjectRecord): string | null {
  if (project.units === null) {
    return null;
  }

  if (project.units === 1 && project.typeCategory !== 'residential') {
    return '1 structure';
  }

  return `${numberFormatter.format(project.units)} ${project.units === 1 ? 'unit' : 'units'}`;
}

function getMetrics(project: ProjectRecord): string[] {
  return [formatSquareFeet(project.squareFeet), formatUnits(project)].filter((value): value is string => Boolean(value));
}

function getDisplayTitle(project: ProjectRecord): string {
  return project.displayTitle ?? project.title;
}

function formatCompletedCount(count: number): string {
  return `${count} completed ${count === 1 ? 'project' : 'projects'}`;
}

function formatLargeNumber(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  }

  if (value >= 1_000) {
    return `${Math.round(value / 1_000)}K`;
  }

  return numberFormatter.format(value);
}

function getArchiveTotals(groups: ArchiveLocationGroup[]) {
  return groups.reduce(
    (totals, locationGroup) => {
      locationGroup.groups.forEach((group) => {
        group.projects.forEach((project) => {
          totals.squareFeet += project.squareFeet ?? 0;
          totals.units += project.units ?? 0;
        });
      });

      return {
        ...totals,
        markets: totals.markets + 1,
        projects: totals.projects + locationGroup.completedCount,
      };
    },
    { markets: 0, projects: 0, squareFeet: 0, units: 0 },
  );
}

function getLocationTotals(locationGroup: ArchiveLocationGroup[]) {
  return locationGroup.reduce(
    (totals, group) => {
      group.groups.forEach((typeGroup) => {
        typeGroup.projects.forEach((project) => {
          totals.squareFeet += project.squareFeet ?? 0;
          totals.units += project.units ?? 0;
        });
      });

      return totals;
    },
    { squareFeet: 0, units: 0 },
  );
}

interface ProjectArchiveRowProps {
  project: ProjectRecord;
  inset?: boolean;
  isLast?: boolean;
}

function ProjectArchiveRow({ project, inset = false, isLast = false }: ProjectArchiveRowProps) {
  const metrics = getMetrics(project);
  const Icon = categoryIcons[project.typeCategory];

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
        gap: { xs: 1.5, sm: 2 },
        p: { xs: 2.25, sm: 2.75 },
        ml: inset ? { xs: 1.5, sm: 4 } : 0,
        mb: isLast ? 0 : 1.75,
        overflow: 'hidden',
        background: inset
          ? alpha(theme.palette.primary.main, 0.035)
          : `linear-gradient(135deg, #FFFFFF 0%, ${alpha(theme.palette.secondary.light, 0.16)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, inset ? 0.12 : 0.16)}`,
        boxShadow: inset ? 'none' : '0 14px 34px rgba(15, 23, 42, 0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: 4,
          background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          opacity: inset ? 0.35 : 0.9,
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: alpha(theme.palette.primary.main, 0.34),
          boxShadow: inset ? '0 10px 22px rgba(15, 23, 42, 0.05)' : '0 18px 42px rgba(15, 23, 42, 0.1)',
        },
      })}
    >
      <Box
        sx={(theme) => ({
          width: 44,
          height: 44,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        })}
      >
        <Icon fontSize="small" />
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" color="primary.main" sx={{ fontSize: { xs: '1.08rem', md: '1.22rem' }, lineHeight: 1.25 }}>
              {getDisplayTitle(project)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65 }}>
              {project.type}
            </Typography>
          </Box>

          {project.role && (
            <Chip
              label={project.role}
              size="small"
              variant="outlined"
              color="primary"
              sx={(theme) => ({
                flexShrink: 0,
                fontWeight: 700,
                bgcolor: alpha(theme.palette.background.paper, 0.72),
              })}
            />
          )}
        </Box>

        {metrics.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.75 }}>
            {metrics.map((metric) => (
              <Chip
                key={metric}
                label={metric}
                size="small"
                sx={(theme) => ({
                  color: 'text.secondary',
                  bgcolor: alpha(theme.palette.secondary.light, 0.24),
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.16)}`,
                })}
              />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

interface SfrCustomGroupProps {
  projects: ProjectRecord[];
  isLast?: boolean;
}

function SfrCustomGroup({ projects, isLast = false }: SfrCustomGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const totalSquareFeet = projects.reduce((total, project) => total + (project.squareFeet ?? 0), 0);
  const totalUnits = projects.reduce((total, project) => total + (project.units ?? 0), 0);

  return (
    <Box sx={{ mb: isLast ? 0 : 1.75 }}>
      <Paper
        elevation={0}
        sx={(theme) => ({
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.light, 0.2)})`,
          boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
        })}
      >
        <Button
          onClick={() => setExpanded((current) => !current)}
          endIcon={
            <KeyboardArrowDownIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          }
          sx={{
            justifyContent: 'space-between',
            width: '100%',
            px: { xs: 2.25, sm: 2.75 },
            py: 2.5,
            color: 'primary.main',
            textAlign: 'left',
            borderRadius: 0,
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.32)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={(theme) => ({
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.background.paper, 0.7),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              })}
            >
              <VillaIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.08rem', md: '1.22rem' }, lineHeight: 1.25 }}>
                Custom single-family residences
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75, textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}
              >
                A curated set of private homes spanning {numberFormatter.format(totalSquareFeet)} sq ft.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, mr: 1 }}>
            <Chip label={`${projects.length} homes`} size="small" color="primary" />
            <Chip label={`${numberFormatter.format(totalUnits)} units`} size="small" variant="outlined" color="primary" />
          </Box>
        </Button>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={(theme) => ({
              display: 'grid',
              gap: 1.5,
              px: { xs: 1.25, sm: 2 },
              pb: 2,
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            })}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ display: { xs: 'block', md: 'none' }, pt: 1.5, letterSpacing: '0.14em' }}
            >
              {projects.length} homes | {numberFormatter.format(totalUnits)} units
            </Typography>
            {projects.map((project, index) => (
              <ProjectArchiveRow
                key={project.id}
                project={project}
                inset
                isLast={index === projects.length - 1}
              />
            ))}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
}

function splitArchiveGroup(group: ArchiveTypeGroup): Array<{ kind: 'row'; project: ProjectRecord } | { kind: 'sfr'; projects: ProjectRecord[] }> {
  const standardProjects = group.projects.filter((project) => project.groupKey !== SFR_GROUP_KEY);
  const sfrProjects = group.projects.filter((project) => project.groupKey === SFR_GROUP_KEY);

  return [
    ...standardProjects.map((project) => ({ kind: 'row' as const, project })),
    ...(sfrProjects.length > 0 ? [{ kind: 'sfr' as const, projects: sfrProjects }] : []),
  ];
}

function CategoryPanel({ group }: { group: ArchiveTypeGroup }) {
  const archiveItems = splitArchiveGroup(group);
  const Icon = categoryIcons[group.category];

  return (
    <Box sx={{ mt: { xs: 3, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            })}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography
            variant="overline"
            color="primary.main"
            sx={{ display: 'block', fontWeight: 800, letterSpacing: '0.16em' }}
          >
            {group.label}
          </Typography>
        </Box>
        <Chip label={formatCompletedCount(group.projects.length)} size="small" variant="outlined" color="primary" />
      </Box>

      <Box>
        {archiveItems.map((item, index) => {
          const isLast = index === archiveItems.length - 1;

          if (item.kind === 'sfr') {
            return <SfrCustomGroup key={SFR_GROUP_KEY} projects={item.projects} isLast={isLast} />;
          }

          return <ProjectArchiveRow key={item.project.id} project={item.project} isLast={isLast} />;
        })}
      </Box>
    </Box>
  );
}

function LocationCard({ locationGroup }: { locationGroup: ArchiveLocationGroup }) {
  const totals = getLocationTotals([locationGroup]);

  return (
    <Paper
      id={locationGroup.id}
      elevation={0}
      sx={(theme) => ({
        position: 'relative',
        scrollMarginTop: 96,
        mb: { xs: 4.5, md: 6 },
        p: { xs: 2.25, sm: 3, md: 4 },
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
        background: `linear-gradient(160deg, #FFFFFF 0%, ${alpha(theme.palette.primary.main, 0.045)} 52%, ${alpha(theme.palette.secondary.light, 0.18)} 100%)`,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -90,
          right: -90,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: alpha(theme.palette.secondary.light, 0.25),
          pointerEvents: 'none',
        },
      })}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 3, alignItems: 'start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationOnOutlinedIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.18em' }}>
                {locationGroup.location}
              </Typography>
            </Box>
            <Typography variant="h3" color="primary.main" sx={{ fontSize: { xs: '1.65rem', md: '2.25rem' } }}>
              {formatCompletedCount(locationGroup.completedCount)}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, maxWidth: 720 }}>
              {locationGroup.description}
            </Typography>
          </Box>

          <Box
            sx={(theme) => ({
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: '1fr' },
              gap: 1.25,
              minWidth: { md: 190 },
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 4,
              bgcolor: alpha(theme.palette.background.paper, 0.68),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            })}
          >
            {[
              { label: 'Project types', value: locationGroup.groups.length },
              { label: 'Tracked scale', value: `${formatLargeNumber(totals.squareFeet)} sq ft` },
              { label: 'Units / keys', value: numberFormatter.format(totals.units) },
            ].map((item) => (
              <Box
                key={item.label}
                sx={(theme) => ({
                  p: { xs: 1.5, sm: 1.75 },
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.58),
                })}
              >
                <Typography variant="h5" color="primary.main" sx={{ fontSize: '1.15rem' }}>
                  {item.value}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.35, textTransform: 'uppercase', letterSpacing: '0.1em', lineHeight: 1.35 }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {locationGroup.groups.map((group) => (
          <CategoryPanel key={group.category} group={group} />
        ))}
      </Box>
    </Paper>
  );
}

interface FeaturedProjectCardProps {
  project: ProjectRecord;
  imageFetchPriority?: 'high' | 'low' | 'auto';
}

function FeaturedProjectCard({ project, imageFetchPriority }: FeaturedProjectCardProps) {
  const image = project.image ?? '/images/ice3_remastered.png';
  const stem = stemFromPublicImagePath(image);

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
        background: `linear-gradient(160deg, #FFFFFF 0%, ${alpha(theme.palette.primary.main, 0.04)} 48%, ${alpha(theme.palette.secondary.light, 0.18)} 100%)`,
        boxShadow: '0 14px 34px rgba(15, 23, 42, 0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: alpha(theme.palette.primary.main, 0.32),
          boxShadow: '0 18px 42px rgba(15, 23, 42, 0.1)',
        },
      })}
    >
      <Box sx={{ lineHeight: 0, aspectRatio: '10/9', width: '100%', minHeight: 260 }}>
        <Box
          component="img"
          src={portfolioImgSrc(stem, 1280)}
          srcSet={portfolioSrcSet(stem)}
          sizes={FEATURED_MEDIA_SIZES}
          alt={getDisplayTitle(project)}
          loading="lazy"
          decoding="async"
          fetchPriority={imageFetchPriority ?? 'auto'}
          sx={{
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            minHeight: 260,
            display: 'block',
          }}
        />
      </Box>

      <Box sx={{ flex: 1, p: { xs: 2.25, sm: 2.75 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="h4" color="primary.main" sx={{ fontSize: { xs: '1.2rem', md: '1.35rem' }, lineHeight: 1.25 }}>
            {getDisplayTitle(project)}
          </Typography>
          <Chip
            label="Completed Project"
            size="small"
            variant="outlined"
            color="primary"
            sx={(theme) => ({
              flexShrink: 0,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.background.paper, 0.72),
            })}
          />
        </Box>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.25 }}>
          {project.location}
        </Typography>

        {project.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: project.features ? 1.5 : 0 }}>
            {project.description}
          </Typography>
        )}

        {project.features && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {project.features.map((feature) => (
              <Chip
                key={feature}
                label={feature}
                size="small"
                sx={(theme) => ({
                  color: 'text.secondary',
                  bgcolor: alpha(theme.palette.secondary.light, 0.24),
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.16)}`,
                })}
              />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

interface PortfolioArchiveProps {
  groups: ArchiveLocationGroup[];
  featuredProjects: ProjectRecord[];
}

export default function PortfolioArchive({ groups, featuredProjects }: PortfolioArchiveProps) {
  const totals = getArchiveTotals(groups);

  return (
    <Box sx={{ mt: { xs: 7, md: 10 }, mb: { xs: 8, md: 12 } }}>
      <SectionHeading
        eyebrow="Completed Projects"
        title="Delivered work across the Tahoe Basin."
        description="A curated look at flagship projects followed by the full completed-project archive by market."
        align="center"
      />

      <Paper
        elevation={0}
        sx={(theme) => ({
          position: 'relative',
          maxWidth: 1040,
          mx: 'auto',
          mb: { xs: 4, md: 5 },
          p: { xs: 2.5, md: 4 },
          overflow: 'hidden',
          color: 'white',
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 62%, ${theme.palette.secondary.dark} 100%)`,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 16% 18%, rgba(255,255,255,0.16), transparent 28%), radial-gradient(circle at 82% 28%, rgba(255,255,255,0.12), transparent 24%)',
            pointerEvents: 'none',
          },
        })}
      >
        <Box sx={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 3, alignItems: 'center' }}>
          <Box>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.74)', letterSpacing: '0.2em' }}>
              Portfolio at a glance
            </Typography>
            <Typography variant="h3" sx={{ mt: 0.75, fontSize: { xs: '1.6rem', md: '2.2rem' } }}>
              Flagship work and a browsable record of completed places, organized by market.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.25, color: 'rgba(255,255,255,0.76)', maxWidth: 620 }}>
              Start with highlighted projects, then jump between Tahoe-region locations, scan categories, and expand the custom home collection for full detail.
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
            {[
              { value: `${totals.projects}+`, label: 'Completed projects' },
              { value: totals.markets, label: 'Markets' },
              { value: `${formatLargeNumber(totals.squareFeet)}+`, label: 'Sq ft tracked' },
              { value: `${numberFormatter.format(totals.units)}+`, label: 'Units / keys' },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.16)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography variant="h4" sx={{ fontSize: { xs: '1.4rem', md: '1.75rem' } }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {featuredProjects.length > 0 && (
        <Box sx={{ maxWidth: 1040, mx: 'auto', mb: { xs: 5, md: 6 } }}>
          <Typography
            variant="overline"
            color="primary.main"
            sx={{ display: 'block', mb: 2.5, fontWeight: 800, letterSpacing: '0.16em', textAlign: 'center' }}
          >
            Featured completed work
          </Typography>
          <Grid container spacing={{ xs: 2.5, md: 3 }}>
            {featuredProjects.map((project, index) => (
              <Grid item key={project.id} xs={12} md={6}>
                <FeaturedProjectCard project={project} imageFetchPriority={index === 0 ? 'high' : 'auto'} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box sx={{ maxWidth: 1040, mx: 'auto', mb: { xs: 3.5, md: 4.5 }, textAlign: 'center' }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.18em' }}>
          Browse the full archive
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 560, mx: 'auto' }}>
          Explore completed projects by market, category, and scale.
        </Typography>
      </Box>

      <Box
        component="nav"
        aria-label="View projects by region"
        sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mb: { xs: 4.5, md: 6 } }}
      >
        {groups.map((group) => (
          <Link
            key={group.id}
            href={`#${group.id}`}
            underline="none"
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 999,
              color: 'primary.main',
              bgcolor: alpha(theme.palette.background.paper, 0.78),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
              boxShadow: '0 8px 22px rgba(15, 23, 42, 0.05)',
              transition: 'transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                bgcolor: 'background.paper',
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
            })}
          >
            <LocationOnOutlinedIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {group.location}
            </Typography>
          </Link>
        ))}
      </Box>

      <Box sx={{ maxWidth: 1040, mx: 'auto' }}>
        {groups.map((locationGroup) => (
          <LocationCard key={locationGroup.id} locationGroup={locationGroup} />
        ))}
      </Box>
    </Box>
  );
}
